// Die eingelesenen Fassungen eines Werks — EINMAL geladen, von zwei Flächen benutzt.
//
// ⚠️ Warum es diesen Haken jetzt gibt, obwohl `Fassungsliste` bis hierher selbst geladen hat:
// seit der Datei-Deckung je Schalter braucht die Sektion „Audio-Streams" dieselben Daten. Ohne
// gemeinsame Quelle liefen ZWEI Abrufe derselben Route je Bearbeiten-Klick — und schlimmer: zwei
// Zustände, die auseinanderlaufen können. Ein Schalter, der „keine Datei" sagt, während die Liste
// darunter die Datei zeigt, wäre genau die Sorte Widerspruch, die eine Oberfläche unglaubwürdig
// macht.
//
// ⚠️ Damit fällt die frühere Begründung für das späte Laden weg, und das gehört hingeschrieben
// statt stillschweigend geändert: `Fassungsliste` lud beim ersten Aufklappen, weil „bei immer
// offen der Abruf bei jedem Bearbeiten-Klick mitliefe, auch wenn niemand hinsieht". Das galt,
// solange die Liste die einzige Verbraucherin war. Jetzt braucht die Streams-Sektion die Daten
// unabhängig davon, ob die Liste je aufgeklappt wird — der Abruf läuft also ohnehin, und ein
// zweiter, späterer wäre reine Zugabe.
//
// ⛔ Der Haken holt NICHTS zum Abspielen. Signierte Adressen laufen nach 12 h ab; sie kommen bei
// Bedarf aus `GET /api/content/:id/wiedergabe` (Übergabe § 5).
import { useCallback, useEffect, useState } from 'react';
import { audioService } from '../services/audioService';
import { mindBookService } from '../services/mindBookService';
import { getErrorMessage, getErrorStatus } from '../utils/errors';
import type { FassungsUebersicht } from '../types/fassungen';

/**
 * Was auf dem Fehlerpfad ANGEZEIGT wird — getrennt nach Ursache.
 *
 * ⚠️ Der Unterschied ist der ganze Zweck: ein 403 und „dieses Werk hat keine Fassungen" sehen in
 * einer leeren Fläche gleich aus, sind aber gegenteilige Aussagen. Die eine heißt „die Daten sind
 * so", die andere „ich durfte nicht nachsehen".
 */
const fassungsFehlerText = (fehler: unknown): string => {
  const status = getErrorStatus(fehler);
  if (status === 401 || status === 403) {
    return (
      'Diese Übersicht nennt Speicherpfade und ist deshalb auf Verwaltung, Prüfung und Redaktion ' +
      'beschränkt. Die Anmeldung reicht dafür nicht aus — das heißt NICHT, dass das Werk keine ' +
      'Fassungen hat.'
    );
  }
  if (status === 404) {
    return (
      'Dieses Werk ist über die Fassungs-Abfrage nicht erreichbar. Das passiert, wenn es ' +
      'zwischenzeitlich gelöscht wurde — oder wenn es ein Entwurf ist, den dieses Konto nicht ' +
      'sehen darf.'
    );
  }
  if (status === 429) {
    return 'Zu viele Abfragen in kurzer Zeit. Einen Moment warten und erneut versuchen.';
  }
  return `Die Fassungen konnten nicht geladen werden: ${getErrorMessage(fehler)}`;
};

export interface FassungsZustand {
  uebersicht: FassungsUebersicht | null;
  laedt: boolean;
  /** Anzeigetext des Fehlschlags, sonst `null`. Siehe `fassungsFehlerText`. */
  fehler: string | null;
  /** Erneut versuchen — für den Knopf auf dem Fehlerpfad. */
  erneut: () => void;
}

/**
 * @param audioId Kennung des Werks, oder `null` beim Anlegen — dann wird nichts abgerufen.
 *
 * ⚠️ `melde` bekommt der Haken NICHT. Der Balken der Seite gehört der Seite; ein Haken, der von
 * sich aus meldet, meldete auch dann, wenn die aufrufende Fläche den Fehler bereits an Ort und
 * Stelle zeigt — und zweimal dasselbe zu sagen ist schlimmer, als es einmal zu sagen. Wer melden
 * will, liest `fehler` und entscheidet selbst.
 */
/**
 * Woher die Fassungen kommen — die beiden Werktypen haben eigene, gleich gebaute Routen.
 *
 * ⚠️ `mindbook` ist EINZAHL (`/api/mindbook/:id/fassungen`); die Mehrzahl liefert 404. Der Wert
 * steht hier als geschlossene Union und nicht als freier String, damit ein Tippfehler ein
 * Uebersetzungsfehler wird statt eines 404 zur Laufzeit.
 */
export type FassungsQuelle = 'audio' | 'mindbook';

const ABRUF: Record<FassungsQuelle, (id: string) => Promise<FassungsUebersicht>> = {
  audio: (id) => audioService.getFassungen(id),
  mindbook: (id) => mindBookService.getFassungen(id),
};

export function useFassungen(audioId: string | null, quelle: FassungsQuelle = 'audio'): FassungsZustand {
  const [uebersicht, setUebersicht] = useState<FassungsUebersicht | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  const erneut = useCallback(() => setVersuch((n) => n + 1), []);

  useEffect(() => {
    if (!audioId) {
      // Beim Anlegen gibt es weder Kennung noch Dateien. Zustand zurücksetzen, damit die Fläche
      // nicht die Fassungen des ZULETZT bearbeiteten Werks weiterzeigt.
      setUebersicht(null);
      setFehler(null);
      setLaedt(false);
      return;
    }

    // ⚠️ Gegen das Wettrennen beim schnellen Wechsel zwischen zwei Werken: eine spät eintreffende
    // Antwort des vorherigen Werks darf den Zustand des jetzigen nicht überschreiben. Ohne dieses
    // Flag zeigte die Oberfläche gelegentlich fremde Dateien — und zwar ohne jeden Hinweis.
    let gilt = true;
    setLaedt(true);
    setFehler(null);

    ABRUF[quelle](audioId)
      .then((antwort) => {
        if (!gilt) return;
        setUebersicht(antwort);
      })
      .catch((f) => {
        if (!gilt) return;
        console.error('Fassungen konnten nicht geladen werden:', f);
        setUebersicht(null);
        setFehler(fassungsFehlerText(f));
      })
      .finally(() => {
        if (gilt) setLaedt(false);
      });

    return () => {
      gilt = false;
    };
  }, [audioId, quelle, versuch]);

  return { uebersicht, laedt, fehler, erneut };
}
